import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoCloseOutline } from "react-icons/io5";
import { useLogin } from "../services/AuthService";
import "./Login.scss";

interface LoginProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Login({ isOpen, onClose }: LoginProps) {
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const navigate = useNavigate();

  const loginMutation = useLogin();

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!loginIdentifier.trim() || !password) {
      setError("Bitte geben Sie E-Mail/Username und Passwort ein.");
      return;
    }

    loginMutation.mutate(
      { loginIdentifier: loginIdentifier.trim(), password },
      {
        onSuccess: (response) => {
          if (response.error) {
            setError(response.error);
            return;
          }
          console.log("Login erfolgreich", response);
          onClose();
          navigate("/app");
        },
        onError: (err) => {
          console.error("Login fehlgeschlagen", err);

          // Versuche, eine spezifische Fehlermeldung aus der Backend-Antwort zu extrahieren
          const axiosError = err as {
            response?: {
              status?: number;
              data?: {
                code?: string;
                error?: string;
                message?: string;
                detail?: string;
              };
            };
          };

          const status = axiosError?.response?.status;
          const data = axiosError?.response?.data;
          const message = data?.detail || data?.message || data?.error;

          // Nur dieser Fehlercode bedeutet eine ausstehende E-Mail-Bestaetigung.
          if (status === 403 && data?.code === "EMAIL_NOT_VERIFIED") {
            setInfo(
              message || "Bitte bestaetigen Sie zuerst Ihre E-Mail-Adresse.",
            );
            return;
          }

          // 401 = Ungueltige Anmeldedaten
          if (status === 401) {
            setError(message || "Ungueltige Anmeldedaten.");
            return;
          }

          setError(
            message || "Login fehlgeschlagen. Bitte versuchen Sie es erneut.",
          );
        },
      },
    );
  };


  return (
    <div className="login-overlay">
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal__close" onClick={onClose}>
          <IoCloseOutline />
        </button>
        <div className="login-modal__content">
          <h2 className="login-modal__title">Anmelden</h2>
          <p className="login-modal__subtitle">
            Geben Sie Ihre Zugangsdaten ein
          </p>

          <form className="login-modal__form" onSubmit={handleSubmit}>
            <div className="login-modal__field">
              <label htmlFor="loginIdentifier">E-Mail oder Username</label>
              <input
                type="text"
                id="loginIdentifier"
                placeholder="beispiel@mail.de"
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                autoComplete="username"
              />
            </div>

            <div className="login-modal__field">
              <label htmlFor="password">Passwort</label>
              <input
                type="password"
                id="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            {error && <p className="login-modal__error">{error}</p>}

            {info && <p className="login-modal__info">{info}</p>}

            <button
              type="submit"
              className="login-modal__submit"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? "Wird angemeldet..." : "Einloggen"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
